// 🔧 browser_storage_helpers.js
// ✅ Purpose: Standard helper methods for safe browser storage usage across Let's Go Vote
// ✅ Features:
// - Uses app-prefixed storage keys to prevent naming conflicts
// - Keeps sessionStorage focused on temporary navigation and voting flow state
// - Keeps localStorage focused only on harmless UI preferences
// - Prevents large Firestore collections from being cached in browser storage
// - Provides safe JSON parsing helpers
// - Provides clear cleanup helpers for completed flows

// =====================================================
// 🔑 SESSION STORAGE KEYS
// =====================================================

export const SESSION_KEYS = {
  RETURN_TO_AFTER_LOGIN: "lgv_returnToAfterLogin",

  // 🗳 Pending Vote Flow
  PENDING_VOTE_CAR_ID: "lgv_pendingVoteCarId",
  PENDING_VOTE_CATEGORY_ID: "lgv_pendingVoteCategoryId",
  PENDING_VOTE_MODE: "lgv_pendingVoteMode",

  // Legacy vote flow names
  PENDING_VOTE_CID: "lgv_pendingVoteCid",

  // Event navigation
  LAST_EVENT_SLUG: "lgv_lastEventSlug",

  LAST_EVENT_ID: "eventId",

  // Login flow
  LOGIN_WARNING: "lgv_loginWarning",

  // Manager invite flow
  PENDING_MANAGER_ACCEPT: "lgv_pendingManagerAccept",

  VOLUNTEER_ACCEPTED_PREFIX: "lgv_volunteerAccepted_",
  VOTE_CONFIRMATION: "voteConfirmation",
  PENDING_VOTE_ENTRY_LABEL: "pendingVoteEntryLabel",
  PENDING_VOTE_CATEGORY_LABEL: "pendingVoteCategoryLabel", 
  MANAGER_ACCEPTED_EVENT_NAME: "managerAcceptedEventName",
};

export const clearPendingVoteNavigation = () => {
  removeSessionValue(SESSION_KEYS.PENDING_VOTE_CID);
  removeSessionValue(SESSION_KEYS.LAST_EVENT_SLUG);
};

// =====================================================
// 🔑 LOCAL STORAGE KEYS
// =====================================================

export const LOCAL_KEYS = {
  ADMIN_LAST_TAB: "lgv_adminLastTab",
  COLLAPSED_PANELS: "lgv_collapsedPanels",
  ANALYTICS_CONSENT: "lgv_analyticsConsent",
  GA4_EVENT_QUEUE: "lgv_ga4EventQueue",
  GA4_EVENT_VISITS: "lgv_ga4EventVisits",
};

// =====================================================
// 🧰 SAFE SESSION STORAGE HELPERS
// =====================================================

export const setSessionValue = (key, value) => {
  try {
    sessionStorage.setItem(key, String(value));
  } catch (error) {
    console.error("❌ Failed to write sessionStorage value:", key, error);
  }
};

export const getSessionValue = (key, fallback = "") => {
  try {
    return sessionStorage.getItem(key) || fallback;
  } catch (error) {
    console.error("❌ Failed to read sessionStorage value:", key, error);
    return fallback;
  }
};

export const removeSessionValue = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error("❌ Failed to remove sessionStorage value:", key, error);
  }
};

// =====================================================
// 🧰 SAFE LOCAL STORAGE HELPERS
// =====================================================

export const setLocalValue = (key, value) => {
  try {
    localStorage.setItem(key, String(value));
  } catch (error) {
    console.error("❌ Failed to write localStorage value:", key, error);
  }
};

export const getLocalValue = (key, fallback = "") => {
  try {
    return localStorage.getItem(key) || fallback;
  } catch (error) {
    console.error("❌ Failed to read localStorage value:", key, error);
    return fallback;
  }
};

export const removeLocalValue = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error("❌ Failed to remove localStorage value:", key, error);
  }
};

// =====================================================
// 🧹 FLOW CLEANUP HELPERS
// =====================================================

export const clearPendingVoteSession = () => {
  removeSessionValue(SESSION_KEYS.PENDING_VOTE_CAR_ID);
  removeSessionValue(SESSION_KEYS.PENDING_VOTE_CATEGORY_ID);
  removeSessionValue(SESSION_KEYS.PENDING_VOTE_MODE);
};

export const clearLoginRedirectSession = () => {
  removeSessionValue(SESSION_KEYS.RETURN_TO_AFTER_LOGIN);
};
